// @flow
import { Map } from "immutable"
import type { Dispatch } from "redux"
import { createSelector } from "reselect"
import R from "ramda"

// Servies
import cuckoo from "common/services/cuckoo/cuckoo"
import { cuckooEvents } from "common/consts/enums"
import {
  getThirdPartyAncillaries,
  getThirdPartyAncillariesDocuments,
} from "mmb/services/bookings/bookingsApi"
import { bidSelector, currentBookingAdditionalServicesSelector } from "../../../services/selector"
import type { Ancillaries, Documents } from "../consts/types"
import {
  getSeatingFromAncillariesAndAdditionalServices,
  mapperAdditionalServicesToAncillariesSeating,
} from "../../../services/bookingSeating/helpers"
import { mapAncillaryDataToSeating } from "../../../components/BookingSeating/BookingSeating.helpers"

// eslint-disable-next-line flowtype/no-weak-types
type GetState = () => Object

type State = Map<{
  loading: boolean,
  loadingDocuments: boolean,
  ancillaries: Ancillaries,
  documents: Documents,
}>

// CONSTANTS
const LOAD_ANCILLARIES = "ancillaries/LOAD_ANCILLARIES"
const LOAD_ANCILLARIES_SUCCESS = "ancillaries/LOAD_ANCILLARIES_SUCCESS"
const LOAD_ANCILLARIES_FAILURE = "ancillaries/LOAD_ANCILLARIES_FAILURE"
const LOAD_ANCILLARIES_REQUEST = "ancillaries/LOAD_ANCILLARIES_REQUEST"

const LOAD_ANCILLARIES_DOCUMENTS = "ancillaries/LOAD_ANCILLARIES_DOCUMENTS"
const LOAD_ANCILLARIES_DOCUMENTS_SUCCESS = "ancillaries/LOAD_ANCILLARIES_DOCUMENTS_SUCCESS"
const LOAD_ANCILLARIES_DOCUMENTS_FAILURE = "ancillaries/LOAD_ANCILLARIES_DOCUMENTS_FAILURE"
const LOAD_ANCILLARIES_DOCUMENTS_REQUEST = "ancillaries/LOAD_ANCILLARIES_DOCUMENTS_REQUEST"

// REDUCER’s INITIAL STATE
const INITIAL_STATE = Map({
  loading: false,
  error: false,
  loadingDocuments: false,
  ancillaries: {
    fastTrack: null,
    seating: [],
  },
  documents: null,
})

// REDUCER
export default function ancillariesReducer(state: State = INITIAL_STATE, action: any) {
  switch (action.type) {
    case LOAD_ANCILLARIES_REQUEST:
      return state.set("loading", true)
    case LOAD_ANCILLARIES_SUCCESS:
      return state.set("ancillaries", action.res).set("loading", false)
    case LOAD_ANCILLARIES_FAILURE:
      return state.set("loading", false).set("error", true)

    case LOAD_ANCILLARIES_DOCUMENTS_REQUEST:
      return state.set("loadingDocuments", true)
    case LOAD_ANCILLARIES_DOCUMENTS_SUCCESS:
      return state.set("documents", action.res).set("loadingDocuments", false)
    case LOAD_ANCILLARIES_DOCUMENTS_FAILURE:
      return state.set("loadingDocuments", false)

    default:
      return state
  }
}

// SELECTORS
export const ancillariesSelector: $FlowFixMe = (state: any) => state.ancillaries.get("ancillaries")
export const ancillariesDocumentsSelector: $FlowFixMe = (state: any) =>
  state.ancillaries.get("documents")

export const ancillariesLoadingSelector: $FlowFixMe = (state: any) =>
  state.ancillaries.get("loading")

export const ancillariesErrorSelector: $FlowFixMe = (state: any) => state.ancillaries.get("error")

// Fast Track
export const fastTrackAncillarySelector: $FlowFixMe = createSelector(
  ancillariesSelector,
  ancillaries => ancillaries && ancillaries.fastTrack,
)

export const fastTrackSceneSelector: $FlowFixMe = createSelector(
  ancillariesSelector,
  ancillaries => {
    const path = R.lensPath(["fastTrack", 0, "items", 0, "stationCode"])
    const stationCode = R.view(path, ancillaries)

    return stationCode || ""
  },
)

export const seatingAncillarySelector: $FlowFixMe = createSelector(
  ancillariesSelector,
  ancillaries => (ancillaries.seating ? ancillaries.seating : ancillaries.get("seating")),
)

export const shouldDisplayFastTrackAncillarySelector: $FlowFixMe = createSelector(
  fastTrackAncillarySelector,
  ancillariesDocumentsSelector,
  (fastTrack, documents) => Boolean(fastTrack && fastTrack.length > 0 && documents),
)

export const orderedSeatingForSeatingModalSelector: $FlowFixMe = createSelector(
  [currentBookingAdditionalServicesSelector, seatingAncillarySelector],
  (additionalServices, ancillariesSeating) => {
    const services = mapperAdditionalServicesToAncillariesSeating(additionalServices)

    const all = getSeatingFromAncillariesAndAdditionalServices(services, ancillariesSeating)

    return mapAncillaryDataToSeating(all)
  },
)

// eslint-disable-next-line flowtype/no-weak-types
export const loadAncillaries = () => async (dispatch: Dispatch<*>, getState: GetState) => {
  const state = getState()
  const bid = bidSelector(state)
  const promise = getThirdPartyAncillaries(bid)

  // Dispatch action
  await dispatch({
    type: LOAD_ANCILLARIES,
    promise,
    _track: { bid },
  })

  // Get ancillaries response
  const response = await promise

  // Get documents
  if (response && response.fastTrack && response.fastTrack[0]) {
    const orderId = response.fastTrack[0].thirdPartyAncillaryOrderId

    if (orderId) {
      const ok = await dispatch({
        type: LOAD_ANCILLARIES_DOCUMENTS,
        promise: getThirdPartyAncillariesDocuments(bid, orderId),
        _track: { bid, orderId },
      })

      // Track document error
      if (!ok) {
        cuckoo.track(cuckooEvents.MMB_ANCILLARIES_DOCUMENTS_ERROR_XHR)
      }
    }
  }

  // Track ancillaries errro
  if (!response) {
    cuckoo.track(cuckooEvents.MMB_ANCILLARIES_ERROR_XHR)
  }
}
